import { Inject, Injectable } from '@angular/core'
import { MapService } from 'src/app/core/services/map.service'
import {
  VectorSourceSpecification,
  LineLayerSpecification,
  ExpressionSpecification,
  SymbolLayerSpecification,
} from 'maplibre-gl'
import { Subject, takeUntil } from 'rxjs'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { AppConfigModel } from 'src/app/core/models'
import { APP_CONFIG } from 'src/app/app.config'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { Layers } from '../layers-config/layers'
import { getMetricFromMap } from '../metrics/rollouts-map-metrics'
import { DataRolloutTilesService } from '../services/data-rollout-tiles-service'
import { ToastService } from '../services/toast.service'
import { formatDateToYYYYMMDD } from '../utils/date.utils'
import { MapIcons } from 'src/app/core/enums/map-icons.enum'
import { withSignTypeFilter } from '../utils/property-filter.utils'

@Injectable()
export class LayerRolloutsTileService {
  public layerId = Layers.RolloutsTile.name
  public rolloutsTileSourceId = 'static-rollouts-map-tile-source'
  public rolloutsTileLayerId = `${Layers.RolloutsTile.name}-segments`
  public rolloutDirectionLayerId = `${Layers.RolloutsTile.name}-direction`
  public sourceLayerName = 'rollout'
  public isLayerVisible = false
  public isRebuilding = false
  private cleanUp$ = new Subject<void>()
  private minZoom = 5
  private layerInitialized: boolean = false
  private currentDatasource: string = ''
  public selectedFeatureId: string = ''
  private colorExpression: ExpressionSpecification | string = '#a9a9a9'
  private timestamp: Date | undefined

  private rolloutsTileBaseFilter: ExpressionSpecification = ['all']

  private rolloutsTileVectorSource: VectorSourceSpecification | null = null

  private rolloutsTileDataLayerSpecification: LineLayerSpecification = {
    id: this.rolloutsTileLayerId,
    minzoom: this.minZoom,
    type: 'line',
    source: this.rolloutsTileSourceId, // reference the data source
    'source-layer': this.sourceLayerName,
    filter: this.rolloutsTileBaseFilter,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
      visibility: 'none',
      'line-sort-key': [
        'case',
        [
          'any',
          ['==', ['get', 'signId'], this.selectedFeatureId || ''],
          ['==', ['to-string', ['id']], this.selectedFeatureId || ''],
        ],
        10000, // High value to bring selected feature to front
        0, // Use existing isFocused or default to 0
      ],
    },

    paint: {
      'line-color': [
        'interpolate',
        ['linear'],
        ['get', 'speedLimit'],
        0,
        'green',
        80,
        'yellow',
        150,
        'red',
      ],
      'line-width': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        6,
        [
          'any',
          ['==', ['get', 'signId'], this.appState.getState().selectedFeatureId || ''],
          ['==', ['to-string', ['id']], this.appState.getState().selectedFeatureId || ''],
        ],
        6,
        2,
      ],
    },
  }

  private rolloutDirectionLayerSpecification: SymbolLayerSpecification = {
    id: this.rolloutDirectionLayerId,
    source: this.rolloutsTileSourceId,
    'source-layer': this.sourceLayerName,
    minzoom: this.minZoom,
    type: 'symbol',
    filter: this.getDirectionSelectionFilter(this.selectedFeatureId),
    layout: {
      'symbol-placement': 'line',
      'symbol-spacing': 100,
      'icon-size': 0.8,
      'icon-image': MapIcons.ARROW_LEFT,
      'icon-rotate': 180,
      'icon-rotation-alignment': 'map',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  }

  constructor(
    private mapService: MapService,
    private mapEventsService: MapEventsService,
    private appState: AppStateService,
    private dataRolloutTilesService: DataRolloutTilesService,
    private toastService: ToastService,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {
    this.currentDatasource =
      this.appState.selectDatasource() ||
      this.appState.authService.getUserClaims().userDatasources[0] ||
      ''
    if (mapService.isMapReady) {
      this.updateLayer()
    } else {
      this.mapService.mapReadyChanged.subscribe(() => {
        this.updateLayer()
      })
    }
    this.appState.stateChanged.pipe(takeUntil(this.cleanUp$)).subscribe((state) => {
      // If a previous load failed (layerInitialized=false) but the timestamp or
      // datasource changed, retry loading the layer.
      if (!this.layerInitialized && this.appState.hasTimestampChanged()) {
        this.updateLayer()
        return
      }
      if (!this.layerInitialized && this.appState.hasDatasourceParamChanged()) {
        this.updateLayer()
        return
      }
      if (this.layerInitialized) {
        this.evaluateState()
      }
    })
  }

  ngOnDestroy() {
    this.mapService.map.removeFeatureState({
      source: this.rolloutsTileSourceId,
      sourceLayer: this.sourceLayerName,
    })
    this.cleanUp$.next()
    this.mapEventsService.detachMapEvents({ layerId: this.rolloutsTileLayerId })
    this.mapService.map?.removeLayer(this.rolloutsTileLayerId)
    this.mapService.map?.removeLayer(this.rolloutDirectionLayerId)
  }

  private async updateLayer() {
    if (!this.currentDatasource) {
      this.rebuildForNewRollout()
      this.toastService.show({
        type: 'warning',
        message:
          'No datasources are available for your account. Please contact your administrator.',
        delay: 7000,
      })
      return
    }
    // Wait for source to be initialized
    this.timestamp = this.appState.selectTimestamp()
    this.currentDatasource = this.appState.selectDatasource() || this.currentDatasource
    try {
      this.rolloutsTileVectorSource = await this.selectVectorSource(this.timestamp)
    } catch {
      this.rolloutsTileVectorSource = null
    }
    if (this.rolloutsTileVectorSource != null) {
      this.rebuildForNewRollout()
    } else {
      // Remove old layers so the user sees the screen is cleared
      this.rebuildForNewRollout()
      this.toastService.show({
        type: 'warning',
        message: `No rollouts available for the selected datasource${this.timestamp ? ` on ${formatDateToYYYYMMDD(this.timestamp)}` : ''}.`,
        delay: 5000,
      })
      return
    }

    // source is used in rollouts and matched signs layer, so we need to add it only once
    if (!this.mapService.map?.getSource(this.rolloutsTileSourceId)) {
      this.mapService.map?.addSource(this.rolloutsTileSourceId, {
        ...this.rolloutsTileVectorSource!,
        promoteId: 'signId',
      })
    }

    // Find the insertion point - we want to be behind all sign layers
    const beforeLayerId = this.findSignLayerInsertionPoint()

    if (beforeLayerId) {
      // Insert the rollout tile layer before the first matching sign layer
      this.mapService.map?.addLayer(this.rolloutsTileDataLayerSpecification, beforeLayerId)
      this.mapService.map?.addLayer(this.rolloutDirectionLayerSpecification)
    } else {
      // No matching sign layer found, add to the top
      this.mapService.map?.addLayer(this.rolloutsTileDataLayerSpecification)
      this.mapService.map?.addLayer(this.rolloutDirectionLayerSpecification)
    }

    this.mapEventsService.registerForMapEvents({ layerId: this.rolloutsTileLayerId }, true)
    this.layerInitialized = true
    this.applySignTypeFilter()
    this.evaluateState()
  }

  private findSignLayerInsertionPoint(): string | undefined {
    const layers = this.mapService.map?.getStyle()?.layers || []

    // Look for specific layer name patterns that should be above rollout tiles
    const signLayerPatterns = [
      Layers.RolloutsFilteredMatchedSignsTile.name,
      Layers.RolloutsMatchedSignsTile.name,
      Layers.MatchedSigns.name,
      Layers.ClusteredSigns.name,
      Layers.Provider1Observations.name,
      Layers.Provider2Observations.name,
    ]

    let firstMatchingLayer: string | undefined

    // Find the FIRST layer that matches our patterns (so we can insert before it)
    for (const layer of layers) {
      // Check for the specific layer types we're looking for
      if (signLayerPatterns.some((pattern) => layer.id.includes(pattern))) {
        firstMatchingLayer = layer.id
        break // Stop at the first match
      }
    }

    if (firstMatchingLayer) {
      console.log('Found first matching sign layer:', firstMatchingLayer)
      // Return the first matching layer to insert before it
      // This puts our rollout tile layer behind all sign layers
      return firstMatchingLayer
    }
    return undefined
  }

  public rebuildForNewRollout(): void {
    // Ensure the source exists
    const map = this.mapService.map
    if (!map) return

    // Detach events first
    this.mapEventsService.detachMapEvents({ layerId: this.rolloutsTileLayerId })

    // Remove layer explicitly
    if (map.getLayer(this.rolloutsTileLayerId)) {
      map.removeLayer(this.rolloutsTileLayerId)
      map.removeLayer(this.rolloutDirectionLayerId)
    }

    // Remove source (can only be removed after layer is removed)
    if (map.getSource(this.rolloutsTileSourceId)) {
      map.removeSource(this.rolloutsTileSourceId)
    }

    // Reset internal state
    this.layerInitialized = false
  }

  private async selectVectorSource(timestamp: Date | undefined) {
    return await this.dataRolloutTilesService.getRolloutByTimestamp(
      timestamp,
      this.currentDatasource,
    )
  }

  private async evaluateState() {
    if (this.appState.selectLayers().some((layer) => layer === Layers.RolloutsTile.name)) {
      this.setVisibility(true)
    } else {
      this.setVisibility(false)
    }

    // if layer is not visible stop all events
    if (this.isLayerVisible === false) return

    this.applySignTypeFilter()

    if (this.appState.getState().metrics) {
      const rolloutsMapMetrics = this.appState.selectMetrics(Layers.RolloutsTile.name)
      if (rolloutsMapMetrics) {
        this.setColorExpression(
          getMetricFromMap(rolloutsMapMetrics[0]).expression as ExpressionSpecification,
        )
      }
    }

    if (
      this.appState.selectDatasource() !== this.currentDatasource &&
      this.appState.hasDatasourceParamChanged()
    ) {
      this.updateLayer()
      return
    }

    // Compare with the loaded timestamp: hasTimestampChanged() misses changes made while the layer was hidden
    if (this.appState.selectTimestamp()?.getTime() !== this.timestamp?.getTime()) {
      this.updateLayer()
    }

    if (this.appState.hasSelectedFeatureIdChanged()) {
      this.selectedFeatureId = this.appState.getState().selectedFeatureId ?? ''
      this.mapService.map.setPaintProperty(this.rolloutsTileLayerId, 'line-width', [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        6,
        [
          'any',
          ['==', ['get', 'signId'], this.selectedFeatureId],
          ['==', ['to-string', ['id']], this.selectedFeatureId],
        ],
        6,
        2,
      ])
      this.mapService.map.setLayoutProperty(this.rolloutsTileLayerId, 'line-sort-key', [
        'case',
        [
          'any',
          ['==', ['get', 'signId'], this.selectedFeatureId || ''],
          ['==', ['to-string', ['id']], this.selectedFeatureId || ''],
        ],
        10000, // High value to bring selected feature to front
        0, // Use existing isFocused or default to 0
      ])
      this.mapService.map.setFilter(
        this.rolloutDirectionLayerId,
        this.getDirectionSelectionFilter(this.selectedFeatureId),
      )
      this.applySignTypeFilter()
    }
  }

  private getDirectionSelectionFilter(selectedFeatureId: string): ExpressionSpecification {
    return [
      'any',
      ['==', ['get', 'signId'], selectedFeatureId || ''],
      ['==', ['to-string', ['id']], selectedFeatureId || ''],
    ]
  }

  private applySignTypeFilter() {
    const selectedSignTypes = this.appState.selectSignTypes()
    const propertyFilters = this.appState.selectPropertyFilters()

    if (this.mapService.map.getLayer(this.rolloutsTileLayerId)) {
      this.mapService.map.setFilter(
        this.rolloutsTileLayerId,
        withSignTypeFilter(this.rolloutsTileBaseFilter, selectedSignTypes, propertyFilters),
      )
    }

    if (this.mapService.map.getLayer(this.rolloutDirectionLayerId)) {
      this.mapService.map.setFilter(
        this.rolloutDirectionLayerId,
        withSignTypeFilter(
          this.getDirectionSelectionFilter(this.selectedFeatureId),
          selectedSignTypes,
          propertyFilters,
        ),
      )
    }
  }

  setVisibility(isVisible: boolean): void {
    this.isLayerVisible = isVisible
    if (this.mapService.isMapReady) {
      if (this.mapService.getLayerVisibilty(this.rolloutsTileLayerId) != this.isLayerVisible) {
        this.mapService.setLayerVisibility(this.rolloutsTileLayerId, isVisible)
        this.mapService.setLayerVisibility(this.rolloutDirectionLayerId, isVisible)
      }
    }
  }

  public setColorExpression(expression: ExpressionSpecification | string) {
    // set expression in case map is not ready and map ready event hast to set the color
    this.colorExpression = expression
    if (this.mapService.isMapReady) {
      this.mapService.map.setPaintProperty(this.rolloutsTileLayerId, 'line-color', expression)
    }
  }
}
